import { Download, LockKeyhole, LogOut, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import Button from "./Button.jsx";
import SectionHeader from "./SectionHeader.jsx";
import StatusBadge from "./StatusBadge.jsx";
import { dateShort, dollars } from "./format.js";

const TOKEN_KEY = "ccs_admin_token";
const EMAIL_KEY = "ccs_admin_email";

async function readJsonResponse(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || "Request failed.");
  }
  return data;
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState(() => localStorage.getItem(EMAIL_KEY) || "");
  const [secretKey, setSecretKey] = useState("");
  const [complaints, setComplaints] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  async function login(event) {
    event.preventDefault();
    setLoading(true);
    setMessage("Signing in...");

    try {
      const response = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, secretKey }),
      });
      const data = await readJsonResponse(response);

      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(EMAIL_KEY, data.email);
      setToken(data.token);
      setEmail(data.email);
      setSecretKey("");
      setMessage("");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    setToken("");
    setSecretKey("");
    setComplaints([]);
    setMessage("Signed out.");
  }

  const loadComplaints = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setMessage("Loading complaints...");

    try {
      const response = await fetch("/api/admin/complaints", {
        headers: { authorization: `Bearer ${token}` },
      });
      const data = await readJsonResponse(response);
      setComplaints(data.complaints || []);
      setMessage(data.demo ? "Sample case data is shown until live case storage is connected." : "");
    } catch (error) {
      setMessage(error.message);
      if (/unauthorized/i.test(error.message)) logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  async function exportCsv() {
    setLoading(true);
    setMessage("Preparing export...");

    try {
      const response = await fetch("/api/admin/export.csv", {
        headers: { authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Export failed.");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "complaints.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setMessage("");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadComplaints();
  }, [loadComplaints]);

  if (!token) {
    return (
      <section className="mx-auto max-w-xl px-4 py-14 sm:px-6 lg:px-8">
        <SectionHeader eyebrow="Staff access" title="Authorized sign in">
          This area is restricted to the approved admin email and private
          secret key.
        </SectionHeader>

        <form
          onSubmit={login}
          className="mt-8 rounded-md border border-civic-line bg-white p-6 shadow-sm"
        >
          <div className="mb-5 flex items-center gap-3">
            <LockKeyhole className="h-6 w-6 text-civic-blue" aria-hidden="true" />
            <div>
              <div className="font-bold text-civic-navy">Admin verification</div>
              <div className="text-sm text-slate-500">
                Use your approved email and secret key.
              </div>
            </div>
          </div>

          <label className="label" htmlFor="admin-email">Admin email</label>
          <input
            id="admin-email"
            type="email"
            className="field"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />

          <label className="label mt-4" htmlFor="admin-secret-key">Secret key</label>
          <input
            id="admin-secret-key"
            type="password"
            className="field"
            value={secretKey}
            onChange={(event) => setSecretKey(event.target.value)}
            autoComplete="current-password"
            required
          />

          {message && (
            <div className="mt-4 rounded-md bg-red-50 p-3 text-sm font-semibold text-signal-red" role="alert">
              {message}
            </div>
          )}

          <Button type="submit" disabled={loading} className="mt-5 w-full">
            {loading ? "Signing in..." : "Sign in"}
          </Button>
        </form>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <SectionHeader eyebrow="Admin dashboard" title="Complaint review queue">
        Signed in as {email}. Review new complaints, monitor status, and
        prepare referrals.
      </SectionHeader>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button type="button" onClick={loadComplaints} disabled={loading} variant="secondary">
          <RefreshCw className="h-4 w-4" aria-hidden="true" /> Refresh
        </Button>
        <Button type="button" onClick={exportCsv} disabled={loading} variant="secondary">
          <Download className="h-4 w-4" aria-hidden="true" /> Export CSV
        </Button>
        <Button type="button" onClick={logout} variant="secondary">
          <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
        </Button>
      </div>

      {message && (
        <div className="mt-5 rounded-md border border-civic-line bg-white p-4 text-sm font-semibold text-slate-700">
          {message}
        </div>
      )}

      <div className="mt-6 overflow-x-auto rounded-md border border-civic-line bg-white shadow-sm">
        <table className="min-w-full divide-y divide-civic-line text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Case</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Consumer</th>
              <th className="px-4 py-3">Business</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Submitted</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-civic-line">
            {complaints.map((complaint) => (
              <tr key={complaint.case_number}>
                <td className="px-4 py-3 font-bold text-civic-navy">{complaint.case_number}</td>
                <td className="px-4 py-3"><StatusBadge status={complaint.status} /></td>
                <td className="px-4 py-3">{complaint.first_name} {complaint.last_name}</td>
                <td className="px-4 py-3">{complaint.business_name}</td>
                <td className="px-4 py-3">{complaint.category}</td>
                <td className="px-4 py-3">{dollars(complaint.amount_disputed)}</td>
                <td className="px-4 py-3">{dateShort(complaint.created_at)}</td>
              </tr>
            ))}
            {complaints.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-slate-500" colSpan={7}>
                  No complaints are available yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
